import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, signal, computed } from '@angular/core';
import { AuthService } from './core/auth/auth.service';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { eRole, environment } from '../environments/environment';
import { MediaMatcher } from '@angular/cdk/layout';
import { UserProfileData } from './shared/models/UserProfileData';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { TopLevelQueryParamHelperService } from './core/services/top-level-query-param-helper.service';
import { NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatListModule, MatNavList } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatMenuModule } from '@angular/material/menu';
import { MatDivider } from '@angular/material/divider';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';

/**
 * The main application component that handles authentication, language translation,
 * and responsive design for the Bihler Digital Hub frontend.
 * 
 * @remarks
 * This component initializes the authentication state, manages user profile data,
 * and handles language changes. It also listens to router events to control the visibility
 * of the login area and the redirect functionality of the logo icon.
 * 
 * @example
 * ```typescript
 * <app-root></app-root>
 * ```
 * 
 * @public
 * @class
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    NgClass,
    RouterOutlet,
    MatIcon,
    MatNavList,
    MatSidenavModule,
    MatDivider,
    MatMenuModule,
    MatToolbarModule,
    MatButtonModule,
    MatListModule,
    MatSlideToggleModule,
    FormsModule,
    MatTooltipModule,
    TranslatePipe
  ]
})
export class AppComponent implements OnInit, OnDestroy {

  private _mobileQueryListener: () => void;

  /** Signal for authentication state */
  public isAuthenticated = signal(false);
  /** Signal for showing the login area */
  public showLoginArea = signal(false);
  /** Signal for allowing redirect to home */
  public redirectToHomeAllowed = signal(false);
  /** Signal for user profile data */
  public userProfileData = signal<UserProfileData | undefined>(undefined);
  /** Flag for development mode */
  public isDevelopmentMode: boolean = !environment.production;
  /** Media query for mobile devices */
  public mobileQuery: MediaQueryList;
  /** Signal for current language */
  public currentLanguage = signal('de');
  /** Signal for headless mode */
  public headless = signal(true);
  /** Signal for dark mode */
  public isDarkMode = signal(false);

  /**
   * Initializes a new instance of the {@link AppComponent} class.
   * @param authService The auth service for interacting with the authentication mechanisms.
   * @param translate The translation service that gets initialized by this component.
   * @param topLevelQueryParamHelperService A service for getting the query parameters in the app component outside of the router outlet.
   * @param router The angular router used to check wether the login and redirect is possible or not.
   * @param changeDetectorRef The change detector used for listening to window width changes.
   * @param media The media matcher for matching the current display width.
   */
  constructor(
    private authService: AuthService,
    private translate: TranslateService,
    private topLevelQueryParamHelperService: TopLevelQueryParamHelperService,
    router: Router,
    changeDetectorRef: ChangeDetectorRef,
    media: MediaMatcher) {
    router.events.forEach(event => {
      if (event instanceof NavigationEnd) {
        this.showLoginArea.set(!event.url.startsWith('/docuconversion'));
        this.redirectToHomeAllowed.set(!event.url.startsWith('/docuconversion'));
      }
    });
    this.currentLanguage.set(navigator.language);
    if (this.currentLanguage().startsWith('de')) {
      translate.use('de');
    }
    this.showLoginArea.set(!router.url.startsWith('/docuconversion'));
    this.redirectToHomeAllowed.set(!router.url.startsWith('/docuconversion'));
    this.mobileQuery = media.matchMedia('(max-width: 460px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addEventListener('change', this._mobileQueryListener);
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      this.isDarkMode.set(true);
      this.toggleTheme();
    }
  }

  /**
   * Initializes the component by fetching query parameters and user authentication details.
   * 
   * This method is called once the component is initialized. It performs the following tasks:
   * 1. Retrieves the top-level query parameter map.
   * 2. Checks if the 'headless' parameter is present and sets the component's `headless` property accordingly.
   * 3. Verifies if the user has a valid access token and updates the `isAuthenticated` property.
   * 4. Fetches the user profile data and assigns it to the `userProfileData` property.
   * 
   * @returns {Promise<void>} A promise that resolves when the initialization is complete.
   */
  async ngOnInit(): Promise<void> {
    var queryParamMap = await this.topLevelQueryParamHelperService.GetTopLevelQueryParamMap();
    var headless = queryParamMap.get('headless');
    if (headless !== null) {
      this.headless.set(headless == 'true');
    }
    else {
      this.headless.set(false);
    }
    var theme = queryParamMap.get('theme');
    if (theme !== null) {
      if (theme == 'dark' && !this.isDarkMode()) {
        this.isDarkMode.set(true);
        this.toggleTheme();
      } else if (theme == 'light' && this.isDarkMode()) {
        this.isDarkMode.set(false);
        this.toggleTheme();
      }
    }
    this.isAuthenticated.set(this.authService.hasValidAccessToken());
    this.userProfileData.set(this.authService.getUserProfileData() ?? undefined);
  }

  /**
   * Triggers the oAuth login process.
   */
  public login(): void {
    this.authService.login();
  }

  /**
   * Triggers the oAuth logout process.
   */
  public logout(): void {
    this.authService.logout();
    // this.store.dispatch(authLogout());
  }

  /**
   * Triggers the oAuth edit user profile process.
   */
  public editUserProfile(): void {
    this.authService.editProfile();
  }

  /**
   * Changes the language used by {@link translate} to the given language.
   * @param language The language to use for translation from now on.
   */
  public useLanguage(language: string): void {
    this.currentLanguage.set(language);
    this.translate.use(language);
  }

  /**
   * Gets a value that determines if the currently logged in user is an Bihler Digital Hub admin.
   */
  public get isAdmin(): boolean {
    if (this.authService.claims !== null) {
      const groups = this.authService.claims.groups as string[];
      return groups.includes(eRole.bihlerAdmin);
    }
    return false;
  }

  /**
   * Toggles the theme of the application between dark mode and light mode.
   * If `isDarkMode` is true, it adds the 'dark-mode' class to the document body.
   * Otherwise, it removes the 'dark-mode' class from the document body.
   */
  public toggleTheme() {
    if (this.isDarkMode()) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }

  /**
   * Lifecycle hook that is called when the component is destroyed.
   * 
   * This method removes the event listener for changes in the mobile query.
   * It ensures that the listener is properly cleaned up to prevent memory leaks.
   */
  ngOnDestroy(): void {
    this.mobileQuery.removeEventListener('change', this._mobileQueryListener);
  }
}
